<script setup>
import {computed, ref} from "vue";
import SvgIcon from "@/components/SvgIcon/SvgIcon.vue";
import axios from "axios";
import {ElMessage} from "element-plus";
import { useI18n } from "vue-i18n";

const { t } = useI18n()

const props = defineProps({
	visible: {
		type: Boolean,
		default: false
	},
	positionId: Number
})

const emit = defineEmits(['update:visible', 'onConfirm'])

const fileInputRef = ref(null)
const selectFile = ref(null)
const loading = ref(false)

const dialogVisible = computed({
	get: () => props.visible,
	set: (e) => emit('update:visible', e)
})

const handleCancel = () => {
	selectFile.value = null
	emit('update:visible', false)
}

const handleChoiceFile = () => {
	fileInputRef.value.value = null
	fileInputRef.value?.click()
}

const handleFileSelect = (e) => {
	selectFile.value = e.target.files[0]
}

const handleConfirm = () => {
	if (!selectFile.value) return
	const formData = new FormData()
	formData.append('file', selectFile.value)
	
	uploadFileFetch(formData)
}

const uploadFileFetch = async (formData) => {
	const res = await axios.post(`/api/api/fileUpload`, formData)
		.catch(err => {
			ElMessage({
				type: 'error',
				message: err.message || t('contact.job.uploadFail')
			})
		})
	console.log('res --------->', res)
	const filePath = res.data.data
	
	submitResumeFetch(filePath)
}

const submitResumeFetch = async (filePath) => {
	const res = await axios.post(
		`/api/api/resume`,
		{
			file: filePath,
			talent_recruitments_id: props.positionId
		}
	).catch(err => {
		ElMessage({
			type: 'error',
			message: err.message || t('contact.job.uploadFail')
		})
	})
	
	ElMessage({
		type: 'success',
		message: t('contact.job.uploadSuccess')
	})
	
	handleCancel()
}
</script>

<template>
	<div>
		<el-dialog
			v-model="dialogVisible"
			v-bind="$attrs"
			class="upload-dialog"
		>
			<div class="content">
				<div class="content__title">
					{{ $t('contact.job.uploadResume') }}
				</div>
				
				<div class="content__upload">
					<div class="choice-file" @click="handleChoiceFile">
						<SvgIcon
							v-if="!selectFile"
							name="choice-file"
							class-name="choice-file__icon"
						/>
						
						<SvgIcon
							v-else
							name="reselection"
							class-name="choice-file__icon"
						/>
					</div>
				</div>
				
				<div class="content__button">
					<div
						class="btn btn--cancel"
						@click="handleCancel"
					>
						{{ $t('contact.job.cancel') }}
					</div>
					
					<div
						v-if="!loading"
						class="btn btn--confirm"
						:class="{
							'btn--confirm--active': selectFile
						}"
						@click="handleConfirm"
					>
						{{ $t('contact.job.confirm') }}
					</div>
					
					<div
						v-if="loading"
						class="btn btn--confirm"
						:class="{
							'btn--confirm--active': true
						}"
					>
						Loading...
					</div>
				</div>
			</div>
			
			<input
				type="file"
				ref="fileInputRef"
				style="display: none;"
				accept=".docx,.doc,.pdf"
				@change="handleFileSelect"
			/>
		</el-dialog>
	</div>
</template>

<style scoped lang="less">
.content {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	&__title {
		text-align: center;
		font-size: 32px;
		color: #3D3D3D;
	}
	
	&__upload {
		
		.choice-file {
			margin-top: 36px;
			
			&__icon {
				width: 320px;
				height: 320px;
			}
			
		}
		
	}
	
	&__button {
		margin-top: 48px;
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		
		.btn {
			padding: 16px 40px;
			font-size: 24px;
			border-radius: 136px;
			cursor: pointer;
			user-select: none;
			
			&--cancel {
				border: 1px solid @primary-color;
				background: linear-gradient(288deg, #D1170E 3%, #F46760 100%), linear-gradient(288deg, #DA241B 0%, #F0554D 100%);
				-webkit-background-clip: text;
				-webkit-text-fill-color: transparent;
				background-clip: text;
				text-fill-color: transparent;
				
				&:hover {
					background: rgba(218, 36, 27, 1);
					-webkit-text-fill-color: #fff;
				}
			}
			
			&--confirm {
				border: 1px solid @primary-color;
				background: linear-gradient(290deg, #D1170E 3%, #F46760 100%), linear-gradient(290deg, #DA241B 0%, #F0554D 100%);
				color: #FFFFFF;
				opacity: .3;
				
				&--active {
					opacity: 1;
				}
			}
		}
	}
}


:deep(.el-dialog) {
	padding: 72px 128px;
	border-radius: 16px;
}
:deep(.el-dialog__header) {
	padding-bottom: 0;
}

@media only screen and (max-width: 768px) {
	.content {
		&__title {
			font-size: 24px;
		}
		
		&__upload {
			
			.choice-file {
				margin-top: 20px;
				
				&__icon {
					width: 220px;
					height: 220px;
				}
				
			}
			
		}
		
		&__button {
			margin-top: 20px;
			justify-content: space-around;
			.btn {
				padding: 8px 20px;
				font-size: 16px;
			}
		}
	}
	
	:deep(.el-dialog) {
		padding: 30px 72px;
	}
}

@media only screen and (max-width: 448px) {
	.content {
		&__title {
			font-size: 18px;
		}
		
		&__upload {
			
			.choice-file {
				
				&__icon {
					width: 120px;
					height: 120px;
				}
				
			}
			
		}
	}
	
	:deep(.el-dialog) {
		padding: 20px;
	}
}
</style>